/**
 * asset_opfs — the main-thread face of the persistent asset cache
 * (settlement LOD0 research L3, 2026-09-03).
 *
 * Packs, tier files and skins are content-addressed, so a returning player
 * should never download them twice. The bytes live in the origin private
 * file system (worker, sync access handles); this module owns the worker,
 * requests durable storage once (`navigator.storage.persist()` — without it
 * the browser may evict under pressure), and hands the asset module
 * `get`/`put`. Everything degrades to the network: no OPFS, a quota error, a
 * worker failure — all answer null/false and count as misses.
 *
 * Opt out: localStorage `atlas:opfs-cache` = 'off' or `?opfsCache=off`.
 * Diag: `__assetCache()`.
 */
import { OPFS_CACHE_BUDGET } from './opfs_index.js';

interface Pending { resolve: (r: { ok: boolean; rejected?: boolean; bytes?: ArrayBuffer; stats?: { entries: number; bytes: number; budget: number } }) => void }

export const opfsStats = { hits: 0, misses: 0, puts: 0, putFailed: 0, putRejected: 0, timeouts: 0, bytesServed: 0, bytesStored: 0, persisted: null as boolean | null, available: false, entries: 0, storedMb: 0, budgetMb: OPFS_CACHE_BUDGET / 1048576 };

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();
let optOut: boolean | null = null;
let persistAsked = false;

export function opfsCacheOptedOut(): boolean {
  if (optOut !== null) return optOut;
  try {
    optOut = (typeof localStorage !== 'undefined' && localStorage.getItem('atlas:opfs-cache') === 'off')
      || (typeof location !== 'undefined' && /[?&]opfsCache=off\b/.test(location.search));
  } catch { optOut = false; }
  return optOut;
}

function available(): boolean {
  return typeof navigator !== 'undefined' && !!(navigator.storage as { getDirectory?: unknown } | undefined)?.getDirectory && typeof Worker !== 'undefined' && !opfsCacheOptedOut();
}

function ensureWorker(): Worker | null {
  if (worker) return worker;
  if (!available()) return null;
  try {
    worker = new Worker(new URL('../workers/opfs_cache_worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (ev: MessageEvent<{ id: number; ok: boolean; rejected?: boolean; bytes?: ArrayBuffer; stats?: { entries: number; bytes: number; budget: number } }>) => {
      const p = pending.get(ev.data.id);
      if (p) { pending.delete(ev.data.id); p.resolve(ev.data); }
    };
    worker.onerror = () => { for (const p of pending.values()) p.resolve({ ok: false }); pending.clear(); };
    opfsStats.available = true;
    if (!persistAsked) {
      persistAsked = true;
      // Durable storage: granted for installed / frequently visited origins; a refusal is fine (evictable cache).
      void navigator.storage.persist?.().then((ok) => { opfsStats.persisted = ok; }).catch(() => { opfsStats.persisted = false; });
    }
  } catch { worker = null; }
  return worker;
}

function call(req: Record<string, unknown>, transfer: ArrayBuffer[] = []): Promise<{ ok: boolean; rejected?: boolean; bytes?: ArrayBuffer; stats?: { entries: number; bytes: number; budget: number } }> {
  const w = ensureWorker();
  if (!w) return Promise.resolve({ ok: false });
  const id = nextId++;
  return new Promise((resolve) => {
    pending.set(id, { resolve });
    try { w.postMessage({ id, ...req }, transfer); } catch { pending.delete(id); resolve({ ok: false }); }
    // A city stream-in writes hundreds of MB in a burst; a sync-handle write of a 40 MB pack under load can take seconds.
    setTimeout(() => { if (pending.has(id)) { pending.delete(id); opfsStats.timeouts++; resolve({ ok: false }); } }, 90_000);
  });
}

/** The cached bytes of a store key, or null (a miss, no cache, or opted out). */
export async function opfsGet(uri: string): Promise<ArrayBuffer | null> {
  if (!available()) return null;
  const r = await call({ op: 'get', uri });
  if (r.ok && r.bytes) { opfsStats.hits++; opfsStats.bytesServed += r.bytes.byteLength; return r.bytes; }
  opfsStats.misses++;
  return null;
}

/** Store a copy of freshly fetched bytes (the caller keeps using its own buffer). Fire-and-forget. */
export function opfsPut(uri: string, bytes: ArrayBuffer): void {
  if (!available()) return;
  const copy = bytes.slice(0);
  void call({ op: 'put', uri, bytes: copy, budget: OPFS_CACHE_BUDGET }, [copy]).then((r) => {
    if (r.ok) { opfsStats.puts++; opfsStats.bytesStored += bytes.byteLength; }
    else if (r.rejected) opfsStats.putRejected++; // not content-addressed or over the object cap — by design
    else opfsStats.putFailed++;
  });
}

/** Fetch through the cache: OPFS hit, else network then store. */
export async function fetchCached(url: string, uri: string): Promise<ArrayBuffer> {
  const hit = await opfsGet(uri);
  if (hit) return hit;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${res.status} ${uri}`);
  const bytes = await res.arrayBuffer();
  opfsPut(uri, bytes);
  return bytes;
}

/** Diagnostics (`__assetCache()`), refreshed from the worker. */
export async function opfsDiag(): Promise<typeof opfsStats> {
  if (available()) {
    const r = await call({ op: 'stats' });
    if (r.ok && r.stats) { opfsStats.entries = r.stats.entries; opfsStats.storedMb = +(r.stats.bytes / 1048576).toFixed(1); opfsStats.budgetMb = r.stats.budget / 1048576; }
  }
  return { ...opfsStats, available: available() };
}

/** Empty the cache (a probe lever). */
export async function opfsClear(): Promise<boolean> { return (await call({ op: 'clear' })).ok; }
